import { Link } from '@mui/material'

import { Icons } from '../../../components'
import * as CreditCard from './credit-card-guest'
import * as DafTransfer from './daf-transfer-guest'
import * as Cheque from './cheque-guest'
import * as BankTransfer from './bank-transfer-guest'
import * as Securities from './securities-guest'
import * as Other from './other-guest'

export const GUEST_PAYMENT_METHOD_TYPE = {
  creditCard: 'credit-card',
  bankTransfer: 'bank-transfer',
  cheque: 'cheque',
  dafTransfer: 'daf-transfer',
  securities: 'securities',
  estateGifts: 'estate-gifts',
  cryptoCurrency: 'crypto-currency',
  flowThroughShares: 'flow-through-shares',
  insuranceGifts: 'insurance-gifts',
  sharesOfPrivateCorporations: 'shares-of-private-corporations',
}

export const GuestPaymentMethodsMeta = {
  [GUEST_PAYMENT_METHOD_TYPE.creditCard]: {
    icon: <Icons.CreditCard />,
    label: 'Credit Card',
    helperText: 'The fastest way to contribute, but standard fees apply.',
    popoverText:
      'Standard credit card fees are 2.2% + $.30 per transaction. Some credit cards are subject to higher fees.',

    paymentClearingType: 'sync',
    supportsRecurring: true,
    supportsScheduled: false,
    PaymentUI: CreditCard.PaymentUI,
    getPaymentValues: CreditCard.getPaymentValues,
    SuccessUI: CreditCard.SuccessUI,
  },
  [GUEST_PAYMENT_METHOD_TYPE.bankTransfer]: {
    icon: <Icons.BuildingColumns />,
    label: 'Bank Transfer',
    helperText: 'Connect to your bank securely and pay no fees.',
    popoverText:
      'A secure and free way to contribute by linking your bank account. Transactions are processed through a trusted third-party provider.',

    paymentClearingType: 'async',
    supportsRecurring: true,
    supportsScheduled: true,
    PaymentUI: BankTransfer.PaymentUI,
    getPaymentValues: BankTransfer.getPaymentValues,
    SuccessUI: BankTransfer.SuccessUI,
  },
  [GUEST_PAYMENT_METHOD_TYPE.cheque]: {
    icon: <Icons.MoneyCheckDollar />,
    label: 'Cheque',
    helperText: 'Mail a cheque for manual processing.',
    popoverText: 'Processing begins once the cheque is received, typically taking 5–7 business days.',

    paymentClearingType: 'manual',
    supportsRecurring: false,
    supportsScheduled: false,
    PaymentUI: Cheque.PaymentUI,
    getPaymentValues: Cheque.getPaymentValues,
    SuccessUI: Cheque.SuccessUI,
  },
  [GUEST_PAYMENT_METHOD_TYPE.dafTransfer]: {
    icon: <Icons.DafTransfer />,
    label: 'DAF Transfer',
    helperText: 'Transfer funds from another Donor-Advised Fund or foundation.',
    popoverText: 'Transfer funds from an existing Donor-Advised Fund (DAF) or foundation. Processing times vary.',

    paymentClearingType: 'manual',
    supportsRecurring: false,
    supportsScheduled: false,
    PaymentUI: DafTransfer.PaymentUI,
    getPaymentValues: DafTransfer.getPaymentValues,
    SuccessUI: DafTransfer.SuccessUI,
  },
  [GUEST_PAYMENT_METHOD_TYPE.securities]: {
    icon: <Icons.Securities />,
    label: 'Securities',
    helperText: 'Donate stocks or mutual funds for a tax-efficient gift.',
    popoverText: (
      <>
        Donating securities allows you to give more while eliminating capital gains tax. Typically processed in 5–10
        business days. Learn more{' '}
        <Link
          href="https://guide.givewise.ca/for-donors-getting-started-with-givewise/making-a-contribution/contribution-payment-methods/securities-and-mutual-funds"
          color="secondary"
          onClick={(e) => e.stopPropagation()}
          target="_blank"
          rel="noopener noreferrer"
          sx={{ '&:visited': { color: 'inherit' } }}
        >
          here
        </Link>
        .
      </>
    ),

    amountType: 'securities',
    paymentClearingType: 'manual',
    supportsRecurring: false,
    supportsScheduled: false,
    PaymentUI: Securities.PaymentUI,
    getPaymentValues: Securities.getPaymentValues,
    SuccessUI: Securities.SuccessUI,
  },
  [GUEST_PAYMENT_METHOD_TYPE.estateGifts]: {
    icon: <Icons.User />,
    label: 'Estate Gifts',
    helperText: 'Include your legacy through a planned gift.',
    popoverText: (
      <>
        Estate Gifts include Bequests, RRSPs, RRIFs, TFSAs{' '}
        <Link
          href="https://guide.givewise.ca/for-donors-getting-started-with-givewise/making-a-contribution/contribution-payment-methods/estate-gifts"
          color="secondary"
          onClick={(e) => e.stopPropagation()}
          target="_blank"
          rel="noopener noreferrer"
          sx={{ '&:visited': { color: 'inherit' } }}
        >
          Learn more
        </Link>
        .
      </>
    ),

    amountType: 'other',
    paymentClearingType: 'manual',
    supportsRecurring: false,
    supportsScheduled: false,
    PaymentUI: Other.PaymentUI,
    getPaymentValues: Other.getPaymentValues,
  },
  [GUEST_PAYMENT_METHOD_TYPE.cryptoCurrency]: {
    icon: <Icons.CircleDollarToSlot />,
    label: 'Cryptocurrency',
    helperText: 'Donate Bitcoin, Ethereum, or other cryptocurrencies.',
    popoverText: (
      <>
        Donating cryptocurrency to your GiveWise Giving Fund is a secure and efficient way to support charities while
        taking advantage of the potential tax benefits of crypto donations.{' '}
        <Link
          href="https://guide.givewise.ca/for-donors-getting-started-with-givewise/making-a-contribution/contribution-payment-methods/cryptocurrency"
          color="secondary"
          onClick={(e) => e.stopPropagation()}
          target="_blank"
          rel="noopener noreferrer"
          sx={{ '&:visited': { color: 'inherit' } }}
        >
          Learn more
        </Link>
        .
      </>
    ),

    amountType: 'other',
    paymentClearingType: 'manual',
    supportsRecurring: false,
    supportsScheduled: false,
    PaymentUI: Other.PaymentUI,
    getPaymentValues: Other.getPaymentValues,
  },
  [GUEST_PAYMENT_METHOD_TYPE.flowThroughShares]: {
    icon: <Icons.Investments />,
    label: 'Flow-Through Shares',
    helperText: 'Maximize tax efficiency with tax-advantaged securities.',
    popoverText: (
      <>
        A Tax-Efficient Giving Strategy for High-Income Donors{' '}
        <Link
          href="https://guide.givewise.ca/for-donors-getting-started-with-givewise/making-a-contribution/contribution-payment-methods/flow-through-mining-shares"
          color="secondary"
          onClick={(e) => e.stopPropagation()}
          target="_blank"
          rel="noopener noreferrer"
          sx={{ '&:visited': { color: 'inherit' } }}
        >
          Learn more
        </Link>
        .
      </>
    ),

    amountType: 'other',
    paymentClearingType: 'manual',
    supportsRecurring: false,
    supportsScheduled: false,
    PaymentUI: Other.PaymentUI,
    getPaymentValues: Other.getPaymentValues,
  },
  [GUEST_PAYMENT_METHOD_TYPE.insuranceGifts]: {
    icon: <Icons.Lock />,
    label: 'Insurance Gifts',
    helperText: 'Use a life insurance policy to support your cause',
    popoverText: (
      <>
        A gift of a life insurance policy to charity can be a valuable gift planning option for Donors if they desire to
        make a significant future donation.{' '}
        <Link
          href="https://guide.givewise.ca/for-donors-getting-started-with-givewise/making-a-contribution/contribution-payment-methods/life-insurance"
          color="secondary"
          onClick={(e) => e.stopPropagation()}
          target="_blank"
          rel="noopener noreferrer"
          sx={{ '&:visited': { color: 'inherit' } }}
        >
          Learn more
        </Link>
        .
      </>
    ),

    amountType: 'other',
    paymentClearingType: 'manual',
    supportsRecurring: false,
    supportsScheduled: false,
    PaymentUI: Other.PaymentUI,
    getPaymentValues: Other.getPaymentValues,
  },
  [GUEST_PAYMENT_METHOD_TYPE.sharesOfPrivateCorporations]: {
    icon: <Icons.Investments />,
    label: 'Private Corp. Shares',
    helperText: 'Donate private company shares for significant impact.',
    popoverText: (
      <>
        Donating private corporation shares can provide significant tax benefits while supporting your cause.{' '}
        <Link
          href="https://guide.givewise.ca/for-donors-getting-started-with-givewise/making-a-contribution/contribution-payment-methods/preferred-shares-of-a-private-corp."
          color="secondary"
          onClick={(e) => e.stopPropagation()}
          target="_blank"
          rel="noopener noreferrer"
          sx={{ '&:visited': { color: 'inherit' } }}
        >
          Learn more
        </Link>
        .
      </>
    ),

    amountType: 'other',
    paymentClearingType: 'manual',
    supportsRecurring: false,
    supportsScheduled: false,
    PaymentUI: Other.PaymentUI,
    getPaymentValues: Other.getPaymentValues,
  },
}

export const guestPaymentMethods = [
  {
    value: GUEST_PAYMENT_METHOD_TYPE.creditCard,
    ...GuestPaymentMethodsMeta[GUEST_PAYMENT_METHOD_TYPE.creditCard],
  },
  {
    value: GUEST_PAYMENT_METHOD_TYPE.bankTransfer,
    ...GuestPaymentMethodsMeta[GUEST_PAYMENT_METHOD_TYPE.bankTransfer],
  },
  {
    value: GUEST_PAYMENT_METHOD_TYPE.securities,
    ...GuestPaymentMethodsMeta[GUEST_PAYMENT_METHOD_TYPE.securities],
  },
  {
    value: GUEST_PAYMENT_METHOD_TYPE.dafTransfer,
    ...GuestPaymentMethodsMeta[GUEST_PAYMENT_METHOD_TYPE.dafTransfer],
  },
  {
    value: GUEST_PAYMENT_METHOD_TYPE.cheque,
    ...GuestPaymentMethodsMeta[GUEST_PAYMENT_METHOD_TYPE.cheque],
  },
  {
    value: GUEST_PAYMENT_METHOD_TYPE.cryptoCurrency,
    ...GuestPaymentMethodsMeta[GUEST_PAYMENT_METHOD_TYPE.cryptoCurrency],
  },
  {
    value: GUEST_PAYMENT_METHOD_TYPE.flowThroughShares,
    ...GuestPaymentMethodsMeta[GUEST_PAYMENT_METHOD_TYPE.flowThroughShares],
  },
  {
    value: GUEST_PAYMENT_METHOD_TYPE.sharesOfPrivateCorporations,
    ...GuestPaymentMethodsMeta[GUEST_PAYMENT_METHOD_TYPE.sharesOfPrivateCorporations],
  },
  {
    value: GUEST_PAYMENT_METHOD_TYPE.insuranceGifts,
    ...GuestPaymentMethodsMeta[GUEST_PAYMENT_METHOD_TYPE.insuranceGifts],
  },
  {
    value: GUEST_PAYMENT_METHOD_TYPE.estateGifts,
    ...GuestPaymentMethodsMeta[GUEST_PAYMENT_METHOD_TYPE.estateGifts],
  },
]
